import { computed, Injectable, signal, Type } from '@angular/core'
import { filter, map, Observable, Subject } from 'rxjs'

export interface SidePanelConfig {
    titleKey: string
    mode: 'create' | 'edit'
    component: Type<unknown>
    inputs?: Record<string, unknown>
}

/** Optional toggle rendered in the panel header next to the title. */
export interface SidePanelHeaderToggle {
    labelKey: string
    checked: boolean
}

interface StackEntry {
    config: SidePanelConfig
    token: number
    /** Stato da ripristinare sul layer precedente quando questo viene poppato. */
    restoredCanSave: boolean
    restoredIsSaving: boolean
    restoredHeaderToggle: SidePanelHeaderToggle | null
    restoredIsNewEntity: boolean
    restoredHeaderSubtitle: string
}

@Injectable({ providedIn: 'root' })
export class SidePanelService {
    private _isOpen = signal(false)
    private _stack = signal<StackEntry[]>([])
    private _tokenCounter = 0

    readonly isOpen = this._isOpen.asReadonly()
    readonly stack = this._stack.asReadonly()

    /** Token del layer attualmente in primo piano (-1 se pannello chiuso). */
    readonly topToken = computed(() => this._stack().at(-1)?.token ?? -1)

    /** Config del layer in primo piano (compatibilità con template esistenti). */
    readonly config = computed(() => this._stack().at(-1)?.config ?? null)

    // Segnali che riflettono SEMPRE lo stato del layer in cima allo stack.
    readonly canSave = signal(false)
    readonly isSaving = signal(false)
    readonly headerToggle = signal<SidePanelHeaderToggle | null>(null)
    /** True finché il layer in cima non ha ancora salvato la prima volta (pilota le label dei bottoni). */
    readonly isNewEntity = signal(true)
    /**
     * Nome dell'entità in modifica/creazione nel layer in cima, mostrato nell'header del pannello
     * accanto al titolo. Il componente ospitato lo tiene aggiornato in binding (vedi gli effect
     * `headerSubtitle.set(...)` nelle *-edit page).
     */
    readonly headerSubtitle = signal<string>('')

    private readonly _saveRequest = new Subject<number>()
    private readonly _saveWithoutCloseRequest = new Subject<number>()
    private readonly _cancelRequest = new Subject<void>()
    private readonly _afterClose = new Subject<{ saved: boolean; token: number; payload?: unknown }>()
    private readonly _headerToggleChange = new Subject<boolean>()

    /**
     * Traccia se il layer `token` ha effettivamente persistito qualcosa (create/update andati a
     * buon fine), impostato da `setResult()` (payload opzionale). La sua sola presenza in questa
     * mappa — indipendentemente dal payload — fa sì che `close()` emetta `saved: true` anche
     * quando il layer viene chiuso con "Annulla" dopo un "Salva" separato: i dati sono comunque
     * cambiati lato server, quindi chi ascolta `afterClose$`/`afterCloseFor()` deve ricaricare.
     */
    private readonly _results = new Map<number, unknown>()

    /** Emette ad ogni chiusura (qualunque layer). Compatibilità con subscribers non-stack. */
    readonly afterClose$: Observable<boolean> = this._afterClose.pipe(map(e => e.saved))
    readonly cancelRequest$ = this._cancelRequest.asObservable()
    readonly headerToggleChange$ = this._headerToggleChange.asObservable()

    /**
     * Emette solo quando il layer identificato da `token` viene chiuso.
     * Da usare in sostituzione di `afterClose$` quando si deve reagire alla chiusura
     * di uno specifico sub-panel aperto con `open()`.
     */
    afterCloseFor(token: number): Observable<boolean> {
        return this._afterClose.pipe(
            filter(e => e.token === token),
            map(e => e.saved),
        )
    }

    /**
     * Come `afterCloseFor()`, ma porta anche l'eventuale payload impostato via `setResult()`
     * per quel token (es. l'entità creata da un sub-panel). `payload` è presente anche quando
     * `saved` è `false`, per coprire il caso "Salva" (senza chiudere) seguito da "Annulla".
     */
    afterCloseWithPayloadFor<T>(token: number): Observable<{ saved: boolean; payload?: T }> {
        return this._afterClose.pipe(
            filter(e => e.token === token),
            map(e => ({ saved: e.saved, payload: e.payload as T | undefined })),
        )
    }

    /**
     * Segna il layer `token` come "ha persistito dati" (payload opzionale, es. l'entità creata).
     * Va chiamato ad ogni create/update andato a buon fine, anche senza chiudere il pannello:
     * garantisce che una successiva "Annulla" propaghi comunque `saved: true` alla chiusura.
     */
    setResult<T>(token: number, payload?: T): void {
        this._results.set(token, payload)
    }

    /**
     * Emette solo quando il pannello con `token` è in cima allo stack e l'utente
     * preme il pulsante "Salva" nel footer. Da usare in sostituzione dell'obsoleto
     * `saveRequest$` nei componenti panel-aware.
     */
    saveRequestFor(token: number): Observable<void> {
        return this._saveRequest.pipe(
            filter(t => t === token),
            map(() => undefined as void),
        )
    }

    saveWithoutCloseRequestFor(token: number): Observable<void> {
        return this._saveWithoutCloseRequest.pipe(
            filter(t => t === token),
            map(() => undefined as void),
        )
    }

    /**
     * Apre un nuovo layer nello stack sopra l'eventuale layer corrente.
     * @returns Il token univoco assegnato al layer appena aperto.
     */
    open(config: SidePanelConfig): number {
        const token = ++this._tokenCounter
        this._stack.update(s => [
            ...s,
            {
                config,
                token,
                restoredCanSave: this.canSave(),
                restoredIsSaving: this.isSaving(),
                restoredHeaderToggle: this.headerToggle(),
                restoredIsNewEntity: this.isNewEntity(),
                restoredHeaderSubtitle: this.headerSubtitle(),
            },
        ])
        this._isOpen.set(true)
        this.canSave.set(false)
        this.isSaving.set(false)
        this.headerToggle.set(null)
        this.isNewEntity.set(config.mode === 'create')
        this.headerSubtitle.set('')
        return token
    }

    /**
     * Chiude il layer in cima allo stack.
     * - Se lo stack ha altri layer sottostanti, li rivela ripristinando il loro stato.
     * - Se era l'ultimo layer, avvia l'animazione di chiusura del pannello.
     */
    close(saved = false): void {
        const stack = this._stack()
        if (stack.length === 0) return
        const top = stack.at(-1)!
        const persisted = this._results.has(top.token)
        const payload = this._results.get(top.token)
        this._results.delete(top.token)

        this._afterClose.next({ saved: saved || persisted, token: top.token, payload })

        if (stack.length === 1) {
            // Ultimo layer: animazione chiusura pannello, poi pulizia stack
            this._isOpen.set(false)
            setTimeout(() => this._stack.update(s => s.slice(0, -1)), 300)
        } else {
            // Torna al layer precedente, ripristinando il suo stato
            this._stack.set(stack.slice(0, -1))
            this.canSave.set(top.restoredCanSave)
            this.isSaving.set(top.restoredIsSaving)
            this.headerToggle.set(top.restoredHeaderToggle)
            this.isNewEntity.set(top.restoredIsNewEntity)
            this.headerSubtitle.set(top.restoredHeaderSubtitle)
        }
    }

    triggerSave(): void {
        this._saveRequest.next(this.topToken())
    }

    triggerSaveWithoutClose(): void {
        this._saveWithoutCloseRequest.next(this.topToken())
    }

    requestHeaderToggle(checked: boolean): void {
        this._headerToggleChange.next(checked)
    }

    triggerCancel(): void {
        this._cancelRequest.next()
        this.close(false)
    }
}
